import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AddBundleRequest,
  AddBundleResponse,
  AddLineItemRequest,
  AddLineItemResponse,
  ChargeInvoiceRequest,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  CreatePaymentLinkRequest,
  CreatePaymentLinkResponse,
  DeleteInvoiceRequest,
  DeleteLineItemRequest,
  DeleteLineItemResponse,
  DuplicateInvoiceRequest,
  DuplicateInvoiceResponse,
  FieldMask,
  GetInvoiceRequest,
  GetInvoiceResponse,
  GetUpcomingInvoicePreviewRequest,
  GetUpcomingInvoicePreviewResponse,
  HostService,
  Invoice as ApiInvoice,
  InvoiceApiService,
  ListInvoicesRequest,
  PagedRequestOptions,
  PayInvoiceRequest,
  PaymentLinkItem,
  RemoveBundleRequest,
  RemoveBundleResponse,
  SendInvoiceRequest,
  UpdateBundleRequest,
  UpdateBundleResponse,
  UpdateDefaultTaxRatesRequest,
  UpdateDefaultTaxRatesResponse,
  UpdateInvoiceRequest,
  UpdateInvoiceResponse,
  UpdateLineItemRequest,
  UpdateLineItemResponse,
  VoidInvoiceRequest,
} from '@vendasta/billing';
import { newPartnerServiceContext, SSOService } from '@vendasta/sso';
import { Owner, UrlShortenerService } from '@vendasta/url-shortener';
import { Observable, of } from 'rxjs';
import { catchError, map, share, switchMap, take } from 'rxjs/operators';
import { PagedResponse } from '../core';
import {
  CollectionMethod,
  collectionMethodToApi,
  PaymentMethodType,
  paymentMethodTypesToApi,
} from '../retail-customer-configuration';
import { convertApiInvoice, Invoice, InvoicePDFType, isInvoice, listInvoiceFiltersToApi } from './invoice';
import { ListInvoicesFilter } from './list-invoice';
import { AppliedDiscount } from '../applied_discount';
import { appliedDiscountToApi } from '../applied_discount/applied_discount';

@Injectable({ providedIn: 'root' })
export class InvoiceService {
  constructor(
    private hostService: HostService,
    private http: HttpClient,
    private invoiceApiService: InvoiceApiService,
    private ssoService: SSOService,
    private urlShortenerService: UrlShortenerService,
  ) {}

  addBundle(merchantId: string, invoiceId: string, bundleId: string): Observable<Invoice> {
    return this.invoiceApiService
      .addBundle(
        new AddBundleRequest({
          merchantId,
          invoiceId,
          bundleId,
        }),
      )
      .pipe(map((res: AddBundleResponse) => convertApiInvoice(res.invoice)));
  }

  addLineItem(
    merchantId: string,
    invoiceId: string,
    sku: string,
    amount: number,
    quantity: number,
    description: string,
    taxRates: string[],
    appliedDiscounts?: AppliedDiscount[],
    servicePeriodStart?: Date,
    servicePeriodEnd?: Date,
  ): Observable<Invoice> {
    return this.invoiceApiService
      .addLineItem(
        new AddLineItemRequest({
          merchantId,
          invoiceId,
          sku,
          amount,
          quantity,
          description,
          taxRates,
          discounts: (appliedDiscounts || []).map((d) => appliedDiscountToApi(d)),
          servicePeriodStart,
          servicePeriodEnd,
        }),
      )
      .pipe(map((res: AddLineItemResponse) => convertApiInvoice(res.invoice)));
  }

  charge(
    merchantId: string,
    customerId: string,
    invoiceId: string,
    paymentMethodId: string,
  ): Observable<HttpResponse<null>> {
    return this.invoiceApiService.charge(
      new ChargeInvoiceRequest({
        merchantId,
        customerId,
        invoiceId,
        paymentMethodId,
      }),
    );
  }

  create(merchantId: string, customerId: string, orderId?: string, currencyCode?: string): Observable<string> {
    return this.invoiceApiService
      .create(
        new CreateInvoiceRequest({
          merchantId: merchantId,
          customerId: customerId,
          orderId: orderId,
          currencyCode: currencyCode,
        }),
      )
      .pipe(map((res: CreateInvoiceResponse) => (res ? res.invoiceId : '')));
  }

  createPaymentLink(
    merchantId: string,
    customerId: string,
    lineItems: PaymentLinkItem[],
    currencyCode = '',
    due?: Date,
  ): Observable<string> {
    return this.invoiceApiService
      .createPaymentLink(
        new CreatePaymentLinkRequest({
          merchantId: merchantId,
          customerId: customerId,
          lineItems: lineItems,
          currencyCode: currencyCode,
          due: due,
        }),
      )
      .pipe(map((res: CreatePaymentLinkResponse) => (res ? res.invoiceId : '')));
  }

  delete(merchantId: string, invoiceId: string): Observable<HttpResponse<null>> {
    return this.invoiceApiService.delete(
      new DeleteInvoiceRequest({
        merchantId,
        invoiceId,
      }),
    );
  }

  deleteLineItem(merchantId: string, invoiceId: string, id: string): Observable<Invoice> {
    return this.invoiceApiService
      .deleteLineItem(
        new DeleteLineItemRequest({
          merchantId,
          invoiceId,
          id,
        }),
      )
      .pipe(map((res: DeleteLineItemResponse) => convertApiInvoice(res.invoice)));
  }

  exportCSV(merchantId: string, filters?: ListInvoicesFilter): Observable<Blob> {
    const url = `${this.hostService.hostWithScheme}/invoice/export`;
    return this.http
      .post(
        url,
        {
          merchant_id: merchantId,
          filters: filters,
        },
        {
          observe: 'body',
          responseType: 'blob',
          withCredentials: true,
        },
      )
      .pipe(share());
  }

  generatePDF(merchantId: string, invoiceId: string, documentType: InvoicePDFType): Observable<Blob> {
    const url = `${this.hostService.hostWithScheme}/invoice/generate-pdf`;
    return this.http
      .post(
        url,
        {
          merchant_id: merchantId,
          invoice_id: invoiceId,
          document_type: documentType,
        },
        {
          observe: 'body',
          responseType: 'blob',
        },
      )
      .pipe(share());
  }

  get(merchantId: string, invoiceId: string): Observable<Invoice> {
    return this.invoiceApiService
      .get(
        new GetInvoiceRequest({
          merchantId,
          invoiceId,
        }),
      )
      .pipe(map((res: GetInvoiceResponse) => convertApiInvoice(res.invoice)));
  }

  list(filters: ListInvoicesFilter, cursor: string, pageSize: number): Observable<PagedResponse<Invoice>> {
    const req = new ListInvoicesRequest({
      filters: listInvoiceFiltersToApi(filters),
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.invoiceApiService.list(req).pipe(
      map((response) => {
        const invoices = (response.invoices || []).map((item) => convertApiInvoice(item)).filter(isInvoice);
        return new PagedResponse(invoices, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  pay(merchantId: string, invoiceId: string, reason: string): Observable<HttpResponse<null>> {
    return this.invoiceApiService.pay(
      new PayInvoiceRequest({
        merchantId,
        invoiceId,
        reason,
      }),
    );
  }

  preview(merchantId: string, invoiceId: string): Observable<string> {
    const url = `${this.hostService.hostWithScheme}/retail-invoice/preview`;
    return this.http.post(
      url,
      {
        merchant_id: merchantId,
        invoice_id: invoiceId,
      },
      {
        responseType: 'text',
        withCredentials: true,
      },
    );
  }

  getUpcomingPreview(merchantId: string, customerId: string, invoiceDate?: Date): Observable<Invoice> {
    return this.invoiceApiService
      .getUpcomingInvoicePreview(
        new GetUpcomingInvoicePreviewRequest({
          merchantId,
          customerId,
          invoiceDate,
        }),
      )
      .pipe(map((res: GetUpcomingInvoicePreviewResponse) => convertApiInvoice(res.invoice)));
  }

  removeBundle(
    merchantId: string,
    invoiceId: string,
    bundleId: string,
    bundleInstanceId?: string,
  ): Observable<Invoice> {
    return this.invoiceApiService
      .removeBundle(
        new RemoveBundleRequest({
          merchantId,
          invoiceId,
          bundleId,
          bundleInstanceId,
        }),
      )
      .pipe(map((res: RemoveBundleResponse) => convertApiInvoice(res.invoice)));
  }

  send(merchantId: string, invoiceId: string, emailAddresses: string[]): Observable<HttpResponse<null>> {
    return this.invoiceApiService.send(
      new SendInvoiceRequest({
        merchantId,
        invoiceId,
        emailAddresses,
      }),
    );
  }

  updateBundle(
    merchantId: string,
    invoiceId: string,
    bundleId: string,
    hideBundleItems: boolean,
    bundleInstanceId?: string,
  ): Observable<Invoice> {
    return this.invoiceApiService
      .updateBundle(
        new UpdateBundleRequest({
          merchantId,
          invoiceId,
          bundleId,
          hideBundleItems,
          bundleInstanceId,
        }),
      )
      .pipe(map((res: UpdateBundleResponse) => convertApiInvoice(res.invoice)));
  }

  updateDueDate(merchantId: string, id: string, due: Date): Observable<Invoice> {
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      due,
    });

    const fm = new FieldMask({
      paths: ['due'],
    });

    return this.update(pbInvoice, fm);
  }

  updateIssuedDate(merchantId: string, id: string, issued: Date): Observable<Invoice> {
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      issued,
    });

    const fm = new FieldMask({
      paths: ['issued'],
    });
    return this.update(pbInvoice, fm);
  }

  updateContact(merchantId: string, id: string, contactId: string): Observable<Invoice> {
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      contactId,
    });

    const fm = new FieldMask({
      paths: ['contactId'],
    });

    return this.update(pbInvoice, fm);
  }

  updateAdditionalContacts(merchantId: string, id: string, additionalContactIds: string[]): Observable<Invoice> {
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      additionalContactIds,
    });

    const fm = new FieldMask({
      paths: ['additionalContactIds'],
    });

    return this.update(pbInvoice, fm);
  }

  updateCollectionMethod(merchantId: string, id: string, collectionMethod: CollectionMethod): Observable<Invoice> {
    const apiCollectionMethod = collectionMethodToApi(collectionMethod);
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      collectionMethod: apiCollectionMethod,
    });

    const fm = new FieldMask({
      paths: ['collectionMethod'],
    });

    return this.update(pbInvoice, fm);
  }

  updateMemo(merchantId: string, id: string, memo: string): Observable<Invoice> {
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      memo,
    });

    const fm = new FieldMask({
      paths: ['memo'],
    });

    return this.update(pbInvoice, fm);
  }

  updatePaymentMethodTypes(
    merchantId: string,
    id: string,
    paymentMethodTypes: PaymentMethodType[],
  ): Observable<Invoice> {
    const apiPaymentMethodTypes = paymentMethodTypesToApi(paymentMethodTypes);
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      paymentMethodTypes: apiPaymentMethodTypes,
    });

    const fm = new FieldMask({
      paths: ['paymentMethodTypes'],
    });

    return this.update(pbInvoice, fm);
  }

  updateDefaultPaymentMethod(merchantId: string, id: string, defaultPaymentMethod: string): Observable<Invoice> {
    const pbInvoice = new ApiInvoice({
      merchantId,
      id,
      defaultPaymentMethod,
    });

    const fm = new FieldMask({
      paths: ['defaultPaymentMethod'],
    });

    return this.update(pbInvoice, fm);
  }

  updateDefaultTaxes(merchantId: string, invoiceId: string, taxRateIds: string[]): Observable<Invoice> {
    return this.invoiceApiService
      .updateDefaultTaxRates(
        new UpdateDefaultTaxRatesRequest({
          merchantId,
          invoiceId,
          taxRateIds,
        }),
      )
      .pipe(map((res: UpdateDefaultTaxRatesResponse) => convertApiInvoice(res.invoice)));
  }

  updateLineItem(
    merchantId: string,
    invoiceId: string,
    id: string,
    sku: string,
    amount: number,
    quantity: number,
    description: string,
    taxRates: string[],
    appliedDiscounts?: AppliedDiscount[],
    servicePeriodStart?: Date,
    servicePeriodEnd?: Date,
    fieldMask?: FieldMask,
  ): Observable<Invoice> {
    return this.invoiceApiService
      .updateLineItem(
        new UpdateLineItemRequest({
          merchantId,
          invoiceId,
          id,
          sku,
          amount,
          quantity,
          description,
          taxRates,
          discounts: (appliedDiscounts || []).map((d) => appliedDiscountToApi(d)),
          servicePeriodStart,
          servicePeriodEnd,
          fieldMask,
        }),
      )
      .pipe(map((res: UpdateLineItemResponse) => convertApiInvoice(res.invoice)));
  }

  private update(invoice: ApiInvoice, mask: FieldMask): Observable<Invoice> {
    return this.invoiceApiService
      .update(
        new UpdateInvoiceRequest({
          invoice: invoice,
          fieldMask: mask,
        }),
      )
      .pipe(map((res: UpdateInvoiceResponse) => convertApiInvoice(res.invoice)));
  }

  void(merchantId: string, invoiceId: string, reason: string): Observable<HttpResponse<null>> {
    return this.invoiceApiService.void(
      new VoidInvoiceRequest({
        merchantId,
        invoiceId,
        reason,
      }),
    );
  }

  duplicate(merchantId: string, invoiceId: string): Observable<string> {
    return this.invoiceApiService
      .duplicate(
        new DuplicateInvoiceRequest({
          merchantId,
          invoiceId,
        }),
      )
      .pipe(map((res: DuplicateInvoiceResponse) => (res ? res.invoiceId : '')));
  }

  generateShortLink(merchantId: string, invoiceId: string): Observable<string> {
    return this.ssoService
      .getEntryUrl('VBC', newPartnerServiceContext(merchantId), {
        path: `/invoice/${merchantId}/${invoiceId}`,
        skipGatewayAuthentication: true,
      })
      .pipe(
        switchMap((url) => {
          return this.urlShortenerService.createLink('', url, Owner.OWNER_UNTRACKED, '', false).pipe(
            catchError(() => of(null)),
            map((result) => {
              if (!result) {
                return url;
              }
              return `https://${result.domain}${result.shortCode}`;
            }),
          );
        }),
        catchError(() => {
          throw new Error('failed to generate short link');
        }),
        take(1),
      );
  }
}
